import React, {useEffect, useState} from 'react';
import {FormattedMessage, useIntl} from 'react-intl';
import {SettingsClientInformation_viewer} from './__generated__/SettingsClientInformation_viewer.graphql';
import {Button, Dropdown as DropdownWebComponent} from '@/web-components';
import Dropdown from '../../shared/components/dropdowns/dropdown';
import {BUTTON_COLOR, BUTTON_STYLE} from '../../../constants';
import {MODAL_TYPE, showModal} from '../../shared/components/modals/generic_modal_conductor';
import DirectApi from '../../../directApi';
import {getSalesforceInstanceUrl} from './client-js/settings_clients_util.js';
import Util from '../../shared/util/util';
import UpdateClientMutation from '../../../mutations/update_client_mutation';
import Warning from '../../../components/warning';
import InlineLoader from '../../shared/components/inline-loader/inline_loader';

type SettingsClientIntegrationsProps = {
	viewer: SettingsClientInformation_viewer;
	isProjectClientPage: boolean;
};

const SettingsClientIntegrations = ({viewer, isProjectClientPage}: SettingsClientIntegrationsProps): JSX.Element => {
	const intl = useIntl();
	const {formatMessage} = intl;
	const {client} = viewer.company || {};
	let fetchTimeout: NodeJS.Timeout | null = null;

	const [integrationLoading, setIntegrationLoading] = useState<boolean>(false);
	const [salesforceCustomerOptions, setSalesforceCustomerOptions] = useState<
		{
			value: string;
			label: string;
		}[]
	>([]);
	const [hubspotCompanyOptions, setHubspotCompanyOptions] = useState<
		{
			value: string;
			label: string;
		}[]
	>([]);
	const [pipedriveOrganizationOptions, setPipedriveOrganizationOptions] = useState<
		{
			value: string;
			label: string;
		}[]
	>([]);
	const [sageIntacctContactOptions, setSageIntacctContactOptions] = useState<
		{
			value: string;
			label: string;
		}[]
	>([]);
	const [salesforceInstanceUrl, setSalesforceInstanceUrl] = useState<string>('');
	const [searchingCustomers, setSearchingCustomers] = useState<boolean>(false);

	const fetchSageIntacctCustomers = () => {
		if (viewer.company?.sageIntacctConnectionEnabled) {
			setIntegrationLoading(true);

			DirectApi.Fetch('sage_intacct/unimported_customers').then(res => {
				const options = !res?.customers
					? []
					: res.customers
							.sort((a, b) => {
								if (a.NAME.toUpperCase().trim() > b.NAME.toUpperCase().trim()) {
									return 1;
								} else if (a.NAME.toUpperCase().trim() < b.NAME.toUpperCase().trim()) {
									return -1;
								}
								return 0;
							})
							.map(customer => ({value: parseInt(customer.CUSTOMERID), label: `${customer.NAME}`}));
				setSageIntacctContactOptions(options);
				setIntegrationLoading(false);
			});
		}
	};

	const fetchPipedriveOrganizations = () => {
		if (!viewer.company?.pipedriveCompanyDomain) return;
		else {
			setIntegrationLoading(true);

			DirectApi.Fetch(`pipedrive/unimportedOrganizations`).then(organizations => {
				const options = !Array.isArray(organizations)
					? []
					: organizations
							.sort((a, b) => (a.name.toUpperCase().trim() > b.name.toUpperCase().trim() ? 1 : 0))
							.map(company => ({
								value: company.id.toString(),
								label: company.name,
							}));
				setPipedriveOrganizationOptions(options);
				setIntegrationLoading(false);
			});
		}
	};
	const fetchSalesforceCustomers = () => {
		if (viewer.company?.newSalesforceEnabled) {
			setIntegrationLoading(true);

			DirectApi.Fetch('salesforce/unimported_accounts').then(accounts => {
				if (accounts?.length) {
					const options = accounts.list
						.sort((a, b) => (a.Name.toUpperCase().trim() > b.Name.toUpperCase().trim() ? 1 : -1))
						.map(account => ({
							value: account.Id,
							label: account.Name,
						}));
					setSalesforceCustomerOptions(options);
					setIntegrationLoading(false);
				}
			});
		}
	};

	const fetchSalesforceInstanceUrl = async () => {
		if (viewer.company?.newSalesforceEnabled) {
			setIntegrationLoading(true);

			const instanceUrl = await getSalesforceInstanceUrl();

			setSalesforceInstanceUrl(instanceUrl);
			setIntegrationLoading(false);
		}
	};

	const linkHubspotCompany = option => {
		const callback = () => {
			if (option) {
				Util.CommitMutation(UpdateClientMutation, {
					id: client?.id,
					//option is an array with one element
					hubspotCompanyId: option[0],
				});
			}
		};

		const foundOption = hubspotCompanyOptions.find(hubspotOption => hubspotOption.value === option[0]);
		const optionlabel = foundOption && foundOption.label;

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings.client.link_client',
			warningInformation: [
				<div className="link-client-modal-content">
					<FormattedMessage
						id="settings.client.confirm_link_customer"
						values={{linkName: optionlabel, b: chunks => <strong>{chunks}</strong>}}
					/>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: <FormattedMessage id="common.cancel" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: <FormattedMessage id="common.link" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: callback,
				},
			],
		});
	};

	const unlinkHubspotCustomer = () => {
		const {formatMessage} = intl;

		showModal({
			type: MODAL_TYPE.GENERIC,
			className: 'default-warning-modal',
			content: <Warning messageId="settings.client.hubspot_unlink" />,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						Util.CommitMutation(UpdateClientMutation, {
							id: client?.id,
							hubspotCompanyId: null,
						});
					},
				},
			],
		});
	};

	const linkPipedriveOrganization = option => {
		const callback = () => {
			if (option) {
				Util.CommitMutation(UpdateClientMutation, {
					id: client?.id,
					pipedriveOrganizationId: option.value,
				});
			}
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings.client.link_client',
			warningInformation: [
				<div className="link-client-modal-content">
					<FormattedMessage
						id="settings.client.confirm_link_customer"
						values={{linkName: option.label, b: chunks => <strong>{chunks}</strong>}}
					/>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: <FormattedMessage id="common.cancel" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: <FormattedMessage id="common.link" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: callback,
				},
			],
		});
	};

	const unlinkPipedriveOrganization = () => {
		const {formatMessage} = intl;

		showModal({
			type: MODAL_TYPE.GENERIC,
			className: 'default-warning-modal',
			content: <Warning messageId="settings.client.pipedrive_unlink" />,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						Util.CommitMutation(UpdateClientMutation, {
							id: client?.id,
							pipedriveOrganizationId: null,
						});
					},
				},
			],
		});
	};

	const unlinkSageIntacctCustomer = () => {
		const {formatMessage} = intl;

		showModal({
			type: MODAL_TYPE.GENERIC,
			className: 'default-warning-modal',
			content: <Warning messageId="settings.client.sage_intacct_unlink" />,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						Util.CommitMutation(UpdateClientMutation, {
							id: client?.id,
							sageIntacctCustomerId: null,
						});
					},
				},
			],
		});
	};

	const linkSageIntacctCustomer = option => {
		const callback = () => {
			if (option) {
				Util.CommitMutation(UpdateClientMutation, {
					id: client?.id,
					sageIntacctCustomerId: option.value.toString(),
				});
			}
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings.client.link_client',
			warningInformation: [
				<div className="link-client-modal-content">
					<FormattedMessage
						id="settings.client.confirm_link_customer"
						values={{linkName: option.label, b: chunks => <strong>{chunks}</strong>}}
					/>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: <FormattedMessage id="common.cancel" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: <FormattedMessage id="common.link" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: callback,
				},
			],
		});
	};

	const linkSalesforceCustomer = option => {
		const callback = () => {
			if (option) {
				Util.CommitMutation(UpdateClientMutation, {
					id: client?.id,
					salesforceAccountId: option.value,
				});
			}
		};

		showModal({
			type: MODAL_TYPE.WARNING,
			warningMessageId: 'settings.client.link_client',
			warningInformation: [
				<div className="link-client-modal-content">
					<FormattedMessage
						id="settings.client.confirm_link_customer"
						values={{linkName: option.label, b: chunks => <strong>{chunks}</strong>}}
					/>
				</div>,
			],
			useInfoIcon: true,
			buttons: [
				{
					text: <FormattedMessage id="common.cancel" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: <FormattedMessage id="common.link" />,
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.GREEN,
					callback: callback,
				},
			],
		});
	};

	const unlinkSalesforceCustomer = () => {
		const {formatMessage} = intl;

		showModal({
			type: MODAL_TYPE.GENERIC,
			className: 'default-warning-modal',
			content: <Warning messageId="settings.client.salesforce_unlink" />,
			buttons: [
				{
					text: formatMessage({id: 'common.cancel'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.WHITE,
				},
				{
					text: formatMessage({id: 'common.unlink'}),
					style: BUTTON_STYLE.FILLED,
					color: BUTTON_COLOR.RED,
					callback: () => {
						Util.CommitMutation(UpdateClientMutation, {
							id: client?.id,
							salesforceAccountId: null,
						});
					},
				},
			],
		});
	};

	const getHubspotCompanies = searchInput => {
		if (!viewer.company?.hubspotAccountId || !searchInput) {
			setHubspotCompanyOptions([]);
			setIntegrationLoading(false);
			setSearchingCustomers(false);
			return;
		} else {
			setIntegrationLoading(true);

			DirectApi.Fetch(`hubspot/unimportedCompanies${searchInput ? `?search=${searchInput}` : ''}`).then(companies => {
				const options = !companies.list
					? []
					: companies.list
							.sort((a, b) =>
								a.properties.name.toUpperCase().trim() > b.properties.name.toUpperCase().trim() ? 1 : 0
							)
							.map(company => ({value: company.id, label: company.properties.name}));
				setHubspotCompanyOptions(options);
				setIntegrationLoading(false);
				setSearchingCustomers(false);
			});
		}
	};

	const getHubspotCompaniesWithTimeout = newSearchString => {
		//send the request to hubspot service 1 second and a half after the user is done typing
		if (fetchTimeout) {
			clearTimeout(fetchTimeout);
		}
		setSearchingCustomers(true);
		fetchTimeout = setTimeout(() => {
			getHubspotCompanies(newSearchString);
		}, 1000);
	};

	useEffect(() => {
		fetchSageIntacctCustomers();
		// getHubspotCompanies('');
		fetchSalesforceCustomers();
		fetchSalesforceInstanceUrl();
		fetchPipedriveOrganizations();
	}, []);

	return (
		<div className="client-page settings-app">
			{!isProjectClientPage && viewer.company?.sageIntacctConnectionEnabled ? (
				<fieldset className="client-integrations">
					<div className="top-section">
						<h2>Sage Intacct</h2>
					</div>
					<p className="description">
						{client?.sageIntacctCustomerId ? (
							<>
								<FormattedMessage
									id="settings.client.sage_intacct_customer_linked"
									values={{
										a: chunks => (
											<a
											// href={`#`} Seems like ther is not simple way to construct the link... need to figure this out. For now it's disabled and it just shows the logo.
											>
												{chunks}
											</a>
										),
									}}
								/>
								<div>
									<Button
										onClick={unlinkSageIntacctCustomer.bind(this)}
										size={Button.SIZE.STANDARD}
										variant={Button.VARIANT.RED_FILLED}
									>
										<FormattedMessage id="common.unlink" />
									</Button>
								</div>
							</>
						) : (
							<>
								<FormattedMessage id="settings.client.sage_intacct_customer" />
								<div className="integration-customer-dropdown">
									<Dropdown
										value={null}
										options={sageIntacctContactOptions}
										onChange={val => linkSageIntacctCustomer(val)}
										label={formatMessage({id: 'settings.client.sage_intacct_customer'})}
										placeholder={formatMessage({
											id: 'settings.client.sage_intacct_customer',
										})}
										customHeight={30}
										integrationDropdown={true}
										integrationLoading={integrationLoading}
										integrationRetryToFetch={fetchSageIntacctCustomers.bind(this)}
									/>
								</div>
							</>
						)}
					</p>
				</fieldset>
			) : null}
			{!isProjectClientPage && viewer.company?.hubspotAccountId ? (
				<fieldset className="client-integrations">
					<div className="top-section">
						<h2>Hubspot</h2>
					</div>
					<p className="description">
						{client?.hubspotCompanyId ? (
							<>
								<FormattedMessage
									id="settings.client.hubspot_company_linked"
									values={{
										a: chunks => (
											<a
												href={`https://app.hubspot.com/contacts/${viewer.company?.hubspotAccountId}/company/${client?.hubspotCompanyId}/`}
												target="_blank"
											>
												{chunks}
											</a>
										),
									}}
								/>
								<div>
									<Button
										onClick={unlinkHubspotCustomer.bind(this)}
										size={Button.SIZE.STANDARD}
										variant={Button.VARIANT.RED_FILLED}
									>
										<FormattedMessage id="common.unlink" />
									</Button>
								</div>
							</>
						) : (
							<>
								<FormattedMessage id="settings.client.link_hubspot_company" />
								<div className="integration-customer-dropdown">
									<DropdownWebComponent
										isClearable={false}
										onSelect={v => {
											linkHubspotCompany(v);
										}}
										name={formatMessage({id: 'settings.client.hubspot_company'})}
										searchStringChangedCallback={searchString =>
											getHubspotCompaniesWithTimeout(searchString)
										}
									>
										{searchingCustomers ? (
											<InlineLoader />
										) : (
											hubspotCompanyOptions
												.sort((o1, o2) => (o1.label < o2.label ? -1 : 1))
												.map(option => (
													<DropdownWebComponent.SingleText
														key={option.label}
														value={option.value}
														searchString={option.label}
													>
														{option.label}
													</DropdownWebComponent.SingleText>
												))
										)}
									</DropdownWebComponent>
								</div>
							</>
						)}
					</p>
				</fieldset>
			) : null}
			{!isProjectClientPage && viewer.company?.pipedriveCompanyDomain ? (
				<fieldset className="client-integrations">
					<div className="top-section">
						<h2>Pipedrive</h2>
					</div>
					<p className="description">
						{client?.pipedriveOrganizationId ? (
							<>
								<FormattedMessage
									id="settings.client.pipedrive_organization_linked"
									values={{
										a: chunks => (
											<a
												href={`${viewer.company?.pipedriveCompanyDomain}/organization/${client?.pipedriveOrganizationId}`}
												target="_blank"
											>
												{chunks}
											</a>
										),
									}}
								/>
								<div>
									<Button
										onClick={unlinkPipedriveOrganization.bind(this)}
										size={Button.SIZE.STANDARD}
										variant={Button.VARIANT.RED_FILLED}
									>
										<FormattedMessage id="common.unlink" />
									</Button>
								</div>
							</>
						) : (
							<>
								<FormattedMessage id="settings.client.link_pipedrive_organization" />
								<div className="integration-customer-dropdown">
									<Dropdown
										value={null}
										options={pipedriveOrganizationOptions}
										onChange={val => linkPipedriveOrganization(val)}
										label={formatMessage({id: 'settings.client.pipedrive_organization'})}
										placeholder={formatMessage({
											id: 'settings.client.pipedrive_organization',
										})}
										customHeight={30}
										integrationDropdown={true}
										integrationLoading={integrationLoading}
										integrationRetryToFetch={fetchPipedriveOrganizations.bind(this)}
									/>
								</div>
							</>
						)}
					</p>
				</fieldset>
			) : null}
			{!isProjectClientPage && viewer.company?.newSalesforceEnabled ? (
				<fieldset className="client-integrations">
					<div className="top-section">
						<h2>Salesforce</h2>
					</div>
					<p className="description">
						{client?.salesforceAccountId ? (
							<>
								<FormattedMessage
									id="settings.client.salesforce_account_linked"
									values={{
										a: chunks => (
											<a
												target="_blank"
												href={`${salesforceInstanceUrl}/lightning/r/Account/${client?.salesforceAccountId}/view`}
											>
												{chunks}
											</a>
										),
									}}
								/>
								<div>
									<Button
										onClick={unlinkSalesforceCustomer.bind(this)}
										size={Button.SIZE.STANDARD}
										variant={Button.VARIANT.RED_FILLED}
									>
										<FormattedMessage id="common.unlink" />
									</Button>
								</div>
							</>
						) : (
							<>
								<FormattedMessage id="settings.client.link_salesforce_account" />
								<div className="integration-customer-dropdown">
									<Dropdown
										value={null}
										options={salesforceCustomerOptions}
										onChange={val => linkSalesforceCustomer(val)}
										label={formatMessage({id: 'settings.client.salesforce_account'})}
										placeholder={formatMessage({id: 'settings.client.salesforce_account'})}
										customHeight={30}
										integrationDropdown={true}
										integrationLoading={integrationLoading}
										integrationRetryToFetch={fetchSalesforceCustomers.bind(this)}
									/>
								</div>
							</>
						)}
					</p>
				</fieldset>
			) : null}
		</div>
	);
};

export default SettingsClientIntegrations;
